import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader } from 'lucide-react';
import { getAuthHeaders } from '../context/AuthContext';
import EditItemForm from '../components/stash/EditItemForm';
import { BACKEND_URL } from '../config/api';

/**
 * Full-screen Edit Item Page (used on mobile)
 * Fetches item data and renders the EditItemForm in full-screen mode
 */
const EditItemPage = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const autoOpenChangeImage = searchParams.get('changeImage') === 'true';

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/stash/${itemId}`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });

        if (response.ok) {
          const data = await response.json();
          setItem(data);
        } else {
          const err = await response.json();
          setError(err.detail || 'Failed to load item');
        }
      } catch (err) {
        console.error('Fetch item error:', err);
        setError('Failed to load item');
      } finally {
        setLoading(false);
      }
    };

    if (itemId) {
      fetchItem();
    }
  }, [itemId]);

  const handleSuccess = () => {
    // Navigate back to stash inventory
    navigate('/stash', { state: { refresh: true } });
  };

  const handleCancel = () => {
    navigate(-1); // Go back
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <Loader className="animate-spin mx-auto text-sage" size={32} />
          <p className="mt-3 text-gray-soft">Loading item...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => navigate('/stash')}
            className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark">
            Back to Stash
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-beige z-20">
        <div className="flex items-center gap-3 p-4">
          <button onClick={handleCancel}
            className="p-2 -ml-2 text-charcoal hover:bg-cream rounded-lg transition-colors"
            data-testid="back-btn">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-lg font-medium text-charcoal">Edit Item</h1>
        </div>
      </div>

      {/* Form */}
      <EditItemForm
        item={item}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
        autoOpenChangeImage={autoOpenChangeImage}
        isFullScreen={true}
      />
    </div>
  );
};

export default EditItemPage;
